import BleManager from './BleManagerPlx'; // Migrated to use react-native-ble-plx for all meters
import {DLog} from '../utils/HelperMethods';
import {
  Platform,
  PermissionsAndroid,
} from 'react-native';
import {getData, storeData} from './GeneralStorage';
import {KEYS} from './Constants';
import {trackEvent} from './Analytics';
import {
  category,
  cloudWatchAnalyticsLogger,
  status,
} from './CloudWatchAnalytics';
import moment from 'moment';

//for simulator based testing
const mockDevice = {
  advertising: {
    isConnectable: true,
    localName: '2282TAE2135',
    manufacturerData: {
      CDVType: 'ArrayBuffer',
      bytes: [Array],
      data: 'AgEGBwMKGPX+4P4MCTIyODJUQUUyMTM1AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=',
    },
    serviceData: {},
    serviceUUIDs: ['180a', 'fef5', 'fee0'],
    txPowerLevel: -2147483648,
  },
  id: 'B0:C2:05:07:65:B4',
  name: '2282TAE2135',
  rssi: -45,
};

const mockDeviceData = {
  1: {dateTimeOfReading: '2022-06-18T15:06:00.000Z', glucoseValue: 84},
  2: {dateTimeOfReading: '2022-06-18T15:05:00.000Z', glucoseValue: 98},
  3: {dateTimeOfReading: '2022-06-18T15:04:00.000Z', glucoseValue: 113},
  4: {dateTimeOfReading: '2022-06-18T15:02:00.000Z', glucoseValue: 90},
  5: {dateTimeOfReading: '2022-06-18T15:01:00.000Z', glucoseValue: 113},
  6: {dateTimeOfReading: '2022-04-05T12:24:00.000Z', glucoseValue: 70},
  7: {dateTimeOfReading: '2022-04-05T12:21:00.000Z', glucoseValue: 175},
  8: {dateTimeOfReading: '2022-04-04T11:54:00.000Z', glucoseValue: 91},
  9: {dateTimeOfReading: '2022-04-02T07:21:00.000Z', glucoseValue: 99},
};

export const makeMockDevice = async () => {
  let devices = [];
  devices.push(mockDevice);
  await storeData(KEYS.bluetoothDeviceList, JSON.stringify(devices));
  await storeData(mockDevice.name, JSON.stringify(mockDeviceData));
};

//initialization
export const checkPermissionsAndStart = async (
  setShowPermissionModal,
  automated,
  automatedProcess,
) => {
  let bluetoothScanPermission,
    bluetoothConnectPermission,
    accessFineLocationPermission;
  DLog('Module Started with Platform', Platform.OS, Platform.Version);
  BleManager.start({showAlert: true});
  if (Platform.OS === 'android') {
    if (Platform.Version >= 31) {
      //check for these two permissions only on android version >= 31
      bluetoothScanPermission = await PermissionsAndroid.check(
        PermissionsAndroid.PERMISSIONS.BLUETOOTH_SCAN,
      );
      bluetoothConnectPermission = await PermissionsAndroid.check(
        PermissionsAndroid.PERMISSIONS.BLUETOOTH_CONNECT,
      );
      if (!(bluetoothScanPermission && bluetoothConnectPermission)) {
        setShowPermissionModal(true);
      } else if (automated) {
        automatedProcess();
      }
    } else if (Platform.Version >= 23) {
      accessFineLocationPermission = await PermissionsAndroid.check(
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
      );
      if (!accessFineLocationPermission) {
        setShowPermissionModal(true);
      } else if (automated) {
        automatedProcess();
      }
    }
  }
};

export const requestPermissions = setShowPermissionModal => {
  if (Platform.OS === 'android' && Platform.Version >= 31) {
    PermissionsAndroid.requestMultiple([
      PermissionsAndroid.PERMISSIONS.BLUETOOTH_SCAN,
      PermissionsAndroid.PERMISSIONS.BLUETOOTH_CONNECT,
    ]).then(result => {
      DLog('Permissions', result);
      checkPermissionsAndStart(setShowPermissionModal);
    });
  } else if (Platform.OS === 'android' && Platform.Version >= 23) {
    PermissionsAndroid.request(
      PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
    ).then(result => {
      DLog('Permissions', result);
      checkPermissionsAndStart(setShowPermissionModal);
    });
  }
};

//start the scan for both device types
export const startScan = () => {
  DLog('Start scan invoked');
  // Scan for both Bionime (fee0) and ACON (1808) devices
  // Note: Using empty array [] scans for all devices, then we filter by service UUIDs
  BleManager.scan([], 3, true)
    .then(results => {
      //the results variable is handled by handleDiscoverPeripheral
      DLog('Scanning for both Bionime and ACON devices...');
    })
    .catch(err => {
      DLog('Error scanning...');
      DLog(err);
    });
};

/**
 * Connect to Bionime device, handle pairing, and start sync
 *
 * @deprecated Pairing portion is deprecated (use PairingService instead).
 * Sync portion is still in active use until notification handler conflicts are resolved.
 *
 * Current Status:
 * - Bionime pairing: Should use PairingService (bypassed when USE_NEW_BLE_SERVICES = true)
 * - Bionime sync: Actively in use (USE_NEW_BIONIME_SYNC = false)
 *
 * @see src/bluetooth/pairing/PairingService.ts (for pairing)
 * @see src/bluetooth/sync/BionimeManualSync.ts (for future sync replacement)
 * @see src/bluetooth/CLEANUP_PLAN.md Phase 4
 */
export const connectAndStartNotification = async (
  peripheral,
  setPossiblePairingStartTime = () => {},
  setIsTryingToConnect = () => {},
  setIsTryingToSync = () => {},
  setIsCheckingPairing = () => {},
  onConnectToDeviceSuccess = () => {},
  onConnectionToDeviceFails = () => {},
) => {
  DLog('[OLD FLOW] Using connectAndStartNotification for Bionime device. Pairing should use PairingService; Sync migration pending.');

  try {
    DLog('connectAndFetchName: Connecting to', peripheral);

    // Stop any ongoing scans before attempting to connect
    DLog('Stopping any active scans before connecting...');
    await BleManager.stopScan();

    setPossiblePairingStartTime(new Date().toString());

    if (Platform.OS === 'android') {
      DLog('Checking if device is bonded...');
      let isPeripheralBonded = false;
      let bondState = 'unknown';

      if (BleManager?.getBondState) {
        bondState = await BleManager.getBondState(peripheral.id);
        isPeripheralBonded = bondState === 'bonded';
        DLog('Bond state reported by native module:', bondState);
      } else {
        const bondedPeripherals = await BleManager?.getBondedPeripherals();
        DLog('Bonded peripherals (fallback):', bondedPeripherals);
        isPeripheralBonded = bondedPeripherals?.some(
          bp => bp.id === peripheral.id,
        );
      }

      DLog('Is peripheral bonded?', isPeripheralBonded);

      if (!isPeripheralBonded) {
        DLog('Device not bonded, initiating pairing...');
        setIsTryingToConnect(true);
        setIsTryingToSync(true);
        cloudWatchAnalyticsLogger(
          category.BLUETOOTH_EVENT,
          status.START_PAIR,
          `Started Pairing the device at Datetime ${new Date().toString()}`,
        );
        //connect to the peripheral
        DLog('Attempting to connect to peripheral:', peripheral.id);
        await BleManager.connect(peripheral.id);
        DLog('Connected successfully, waiting for pairing dialog...');
      } else {
        DLog('Device already bonded, connecting directly...');
        setIsTryingToSync(true);
        //connect to the peripheral
        DLog('Attempting to connect to peripheral:', peripheral.id);
        await BleManager.connect(peripheral.id);
        DLog('Connected successfully, starting notifications...');
        //start notifications
        await startNotification(
          peripheral,
          onConnectToDeviceSuccess,
          onConnectionToDeviceFails,
        );
      }
    } else {
      setIsCheckingPairing(true);
      //connect to the peripheral
      await BleManager.connect(peripheral.id);
      //start notifications
      await startNotification(
        peripheral,
        onConnectToDeviceSuccess,
        onConnectionToDeviceFails,
      );
    }
  } catch (err) {
    DLog('ERROR in connectAndStartNotification:', err);
    DLog('Error details:', err?.message, err?.code);
    onConnectionToDeviceFails(err);
  }
};

//starts notification on the fee2 channel
export const startNotification = async (
  peripheral,
  onConnectToDeviceSuccess = () => {},
  onConnectionToDeviceFails = () => {},
) => {
  try {
    //the retrievedServices can be iterated upon but since we know what services to target
    //we wont be using this variable for now
    await BleManager.retrieveServices(peripheral.id);
    //start the notification to get data on the fee2 channel
    BleManager.startNotification(peripheral.id, 'fee0', 'fee2')
      .then(onConnectToDeviceSuccess)
      .catch(onConnectionToDeviceFails);
    // DLog('*** Notification started on ***', peripheral.id, 'fee0', 'fee2');
  } catch (err) {
    onConnectionToDeviceFails(err);
  }
};

export const checkDeviceNameAndStore = async (
  data,
  currentDevice,
  pairingStartTime,
) => {
  let fetchedName = String.fromCharCode
    .apply(null, data.value)
    .substring(1, currentDevice.name.length + 1);
  if (fetchedName && fetchedName == currentDevice.name) {
    //check if the name device sends via notification is not null
    //and then compare it to the currentdevice name from advertising
    if (Platform.OS === 'ios') {
      // PHI boundary: no device ids/serials to third-party analytics
      trackEvent('Pair a Device', {type: 'Bionime'});
      cloudWatchAnalyticsLogger(
        category.BLUETOOTH_EVENT,
        status.START_PAIR,
        `Started Pairing the device at Datetime ${pairingStartTime}`,
      );
      cloudWatchAnalyticsLogger(
        category.BLUETOOTH_EVENT,
        status.PAIR_SUCCESS,
        `Pairing successful at ${new Date().toString()}`,
      );
    }
    DLog('Device is possibly a gluco meter');
    let devicesJSON = await getData(KEYS.bluetoothDeviceList);
    let devices = await JSON.parse(devicesJSON);
    if (!devices) {
      devices = [];
    }
    //adding device only when it is not in local storage
    devices.findIndex(device => device.name === currentDevice.name) === -1 &&
      devices.push(currentDevice);
    await storeData(KEYS.bluetoothDeviceList, JSON.stringify(devices));
    return true;
  } else {
    DLog('Device is NOT a gluco meter');
    return false;
  }
};

export const getStoredDevices = async () => {
  let devices = await getData(KEYS.bluetoothDeviceList);
  return await JSON.parse(devices);
};

export const removeDeviceFromStorage = async peripheral => {
  let devicesJSON = await getData(KEYS.bluetoothDeviceList);
  let devices = await JSON.parse(devicesJSON);
  // Filter by both ID and name to handle devices with missing names
  let newDevices = devices.filter(
    device => device.id !== peripheral.id && device.name !== peripheral.name,
  );
  DLog('Removing device:', peripheral);
  DLog('Devices before:', devices);
  DLog('Devices after:', newDevices);
  await storeData(KEYS.bluetoothDeviceList, JSON.stringify(newDevices));
};

export const disconnectDevices = async () => {
  const devices = await BleManager.getConnectedPeripherals([]);
  DLog(devices);
  for (let i = 0; i < devices.length; i++) {
    let peripheral = devices[i];
    await BleManager.disconnect(peripheral.id);
  }
};

//get Number of Glucose Data Entries
export const getNumberOfGlucoseData = async peripheral => {
  DLog('Getting number of Glucose data for', peripheral.name);

  //first turn on the CPL
  let dataCPL = [0x00];
  await writeMessage(peripheral.id, 'fee0', 'fee1', dataCPL);
  DLog('CPL mode enabled');

  let totalGlucoseData = [0xb0, 0x61, 0x00, 0x00, 0x11];
  await delay(200); // Reduced delay - quick succession keeps connection active
  await writeMessage(peripheral.id, 'fee0', 'fee3', totalGlucoseData);
  DLog('Glucose data count request sent, waiting for device response...');
};

//get Total glucose data
export const getGlucoseData = async (peripheral, numEntries) => {
  //DLog('Getting total Glucose data for', peripheral.name, numEntries);
  let nthGlucoseDataCommand, lowBit, highBit, checkSum;
  for (let i = 1; i <= numEntries; i++) {
    //YES! this array starts at 1 :D
    lowBit = i & 0xff; //TODO: if entries >256, need to compute high bit too
    highBit = (i >> 8) & 0xff;
    checkSum = (0xb0 + 0x61 + lowBit + highBit) & 0xff;
    nthGlucoseDataCommand = [0xb0, 0x61, lowBit, highBit, checkSum];
    //DLog(nthGlucoseDataCommand);
    await writeMessage(peripheral.id, 'fee0', 'fee3', nthGlucoseDataCommand);
    await delay(70); //just the right amount of delay to allow the device to send two data packets
  }
};

export const storeGlucoseData = async (
  currentDevice,
  entry,
  onNewEntryAdded = () => {},
  onEntryStored = () => {},
  onFutureDateReadingFound = () => {},
) => {
  let keyIndex = entry[4];
  let dtBit0 = entry[6];
  let dtBit1 = entry[7];
  let dtBit2 = entry[8];
  let dtBit3 = entry[9];
  let dtBit4 = entry[10];
  let dtBit5 = entry[11];
  let month = ((dtBit1 & 0xc0) >> 4) + ((dtBit0 & 0xc0) >> 6) + 1;
  let day = (dtBit0 & 0x1f) + 1;
  let hour = dtBit1 & 0x1f;
  let minute = dtBit2 & 0x3f;
  let year = (dtBit3 & 0x7f) + 2000;
  let glucoseValue = ((dtBit4 & 0x03) << 8) + dtBit5;
  let dateTimeOfReading = new Date(
    year,
    month - 1,
    day,
    hour,
    minute,
  ).toISOString();

  //checking recent reading time is in present or not
  if (keyIndex === 1) {
    const isFutureReading = moment(dateTimeOfReading).isAfter(
      moment().add(24, 'h'),
    );

    if (isFutureReading) onFutureDateReadingFound();
  }

  let existingData = JSON.parse(await getData(currentDevice.name));
  //DLog('current data', existingData);
  //DLog(existingData);
  if (!existingData) {
    existingData = {};
  }
  //using userId+dateTimeOfReading+glucoseValue as the unique key to support different users in same app
  const userId = await getData(KEYS.userId);
  if (!existingData[userId + dateTimeOfReading + glucoseValue]) {
    //flow for a new reading
    existingData[userId + dateTimeOfReading + glucoseValue] = {
      glucoseValue: glucoseValue,
      dateTimeOfReading: dateTimeOfReading,
      isSynced: 0,
      userId,
    };
    onNewEntryAdded();
  }
  // DLog(existingData);
  await storeData(currentDevice.name, JSON.stringify(existingData));
  onEntryStored();
};

//Helper Methods
//delay promise
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
//write a Generic message
export const writeMessage = async (peripheralId, serviceId, charId, data) => {
  // DLog(
  //   'writeMessage: going to write data:',
  //   peripheralId,
  //   serviceId,
  //   charId,
  //   data,
  // );
  const returnedMessage = await BleManager.write(
    peripheralId,
    serviceId,
    charId,
    data,
  );
  //DLog('writeMessage: command written', peripheralId, serviceId, charId, data);
  //DLog('writeMessage: return from write message', returnedMessage);
};

//Logging functions
//Log a service element
export const logServiceElement = ele => {
  DLog(
    ele,
    '\n',
    ele.characteristic,
    '\n',
    ele.properties,
    '\n',
    ele.service,
    '\n',
    ele.descriptors,
    '\n',
    ele.descriptors ? ele.descriptors[0].uuid : 'no id',
    '\n',
    '\n',
    '\n',
  );
};

//Log a data element from handleUpdateValueForCharacteristic
export const logDataElement = data => {
  // DLog('data on update without transformation', data);
  // DLog(
  //   'Received data from ' +
  //     data.peripheral +
  //     ' characteristic ' +
  //     data.characteristic,
  //   data.value,
  // );
};

//********************* Unused Methods: For quick future reference *********************
//returns a list of devices already paired
export const retrieveConnected = async () => {
  return await BleManager.getConnectedPeripherals([]);
};
